// src/pages/PricingPage.jsx
import React, { useState } from 'react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { Check, ChevronDown, Plus, Zap, Star, ChevronRight, HelpCircle, Rocket } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';
import FlowingReviewsTicker from '../components/FlowingReviewsTicker';
import ReviewForm from '../components/ReviewForm';
import GeometricBackground from '../components/ui/GeometricBackground';
import { trackPricingView, trackPlanSelected, trackCheckoutStart } from '../utils/conversionEvents';
import { useRegionalPricing } from '../hooks/useRegionalPricing';

const PricingPage = ({ onPlanSelect }) => {
  const { t } = useTranslation();
  const { getPriceLabel: getPlanPriceLabel } = useRegionalPricing();
  const navigate = useLocalizedNavigate();

  // SEO: meta title, description, and SoftwareApplication schema for pricing page
  React.useEffect(() => {
    trackPricingView({ source: 'pricing_page' });

    const prevTitle = document.title;
    document.title = 'Pricing | Logami – German Exam Prep Plans';

    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') || '';
    const created = !metaDesc;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', "Compare Logami plans for TELC, Goethe & ÖSD German exam prep. Start free with 1 full mock exam, get 3 months of full access for €39.99, or add a real German teacher with Premium.");

    const ld = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Logami',
      applicationCategory: 'EducationalApplication',
      operatingSystem: 'Any',
      offers: [
        { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'EUR', description: 'Free forever: 1 full mock exam and 1 AI writing correction' },
        { '@type': 'Offer', name: '3 Months', price: '39.99', priceCurrency: 'EUR', description: "90 days of full access: all mock exams, unlimited AI writing feedback, personalized study plan. One-time payment." },
        { '@type': 'Offer', name: 'Premium', price: '149.00', priceCurrency: 'EUR', description: '6 months of full access plus 5 personal Zoom sessions and 10 texts corrected by a German teacher. One-time payment.' },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(ld);
    script.setAttribute('data-seo-pricing', '');
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (created && metaDesc?.parentNode) metaDesc.remove();
      else if (metaDesc) metaDesc.setAttribute('content', prevDesc);
      document.querySelector('script[data-seo-pricing]')?.remove();
    };
  }, []);

  const [mobileCardsRef, mobileCardsAreVisible] = useScrollAnimation();
  const [desktopCardsRef, desktopCardsAreVisible] = useScrollAnimation();
  const [faqRef, faqsAreVisible] = useScrollAnimation();
  const [reviewsRef, reviewsAreVisible] = useScrollAnimation();

  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const pricingFaqs = [
    { q: t('faq.questions.paymentOneTime'), a: t('faq.answers.paymentOneTime') },
    { q: t('faq.questions.paymentAccess'),  a: t('faq.answers.paymentAccessV2')  },
    { q: t('faq.questions.paymentFree'),    a: t('faq.answers.paymentFree')    },
    { q: t('faq.questions.paymentMethods'), a: t('faq.answers.paymentMethods') },
    { q: t('faq.questions.paymentRefund'),  a: t('faq.answers.paymentRefund')  },
  ];

  const normalizeBenefits = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim()) return [value];
    return [];
  };

  const plans = [
    {
      id: 'free',
      name: t('pricing.lineup.free.name'),
      price: '€0',
      cycle: t('pricing.plans.cycles.once'),
      headline: t('pricing.lineup.free.headline'),
      description: t('pricing.lineup.free.description'),
      benefits: normalizeBenefits(t('pricing.lineup.free.benefits', { returnObjects: true })),
      trustLine: t('pricing.lineup.free.trustLine'),
      isPopular: false,
    },
    // The plan we want most people to buy sits in the middle and is the only
    // highlighted card; Premium on its right makes it look like the sensible choice.
    {
      id: 'quarterly',
      name: t('pricing.lineup.quarterly.name'),
      price: getPlanPriceLabel('quarterly'),
      cycle: t('pricing.plans.cycles.quarter'),
      headline: t('pricing.lineup.quarterly.headline'),
      description: t('pricing.lineup.quarterly.description'),
      benefits: normalizeBenefits(t('pricing.lineup.quarterly.benefits', { returnObjects: true })),
      trustLine: t('pricing.lineup.quarterly.trustLine'),
      isPopular: true,
    },
    {
      id: 'premium',
      name: t('pricing.lineup.premium.name'),
      price: getPlanPriceLabel('premium'),
      cycle: t('pricing.lineup.premium.cycle'),
      headline: t('pricing.lineup.premium.headline'),
      description: t('pricing.lineup.premium.description'),
      benefits: normalizeBenefits(t('pricing.lineup.premium.benefits', { returnObjects: true })),
      trustLine: t('pricing.lineup.premium.trustLine'),
      isPopular: false,
      // True: the teacher has about 3 hours a week for Premium students.
      badge: t('pricing.lineup.premium.badge'),
    },
  ];

  const handleMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = '#3A86FF';
    e.currentTarget.style.borderColor = '#3A86FF';
    e.currentTarget.style.color = '#FFFFFF';
    const shine = e.currentTarget.querySelector('.shine-effect');
    if (shine) shine.style.transform = 'translateX(100%)';
  };

  const handleMouseLeave = (e, isPopular) => {
    e.currentTarget.style.backgroundColor = '#000000';
    e.currentTarget.style.borderColor = '#000000';
    e.currentTarget.style.color = '#FFFFFF';
    const shine = e.currentTarget.querySelector('.shine-effect');
    if (shine) shine.style.transform = 'translateX(-100%)';
  };

  const handlePlanSelect = async (plan) => {
    setSelectedPlan(plan);
    trackPlanSelected({ planId: plan.id, context: 'pricing_page' });

    if (plan.id === 'free') {
      // Picking "Free" is just a navigation choice — it must never write to the
      // subscription fields. This previously called initializeFreeTier(), which
      // meant a subscriber clicking the Free card downgraded themselves.
      try {
        const { auth } = await import('../firebase');
        navigate(auth.currentUser ? '/dashboard' : '/auth?mode=signup&redirect=/pricing');
      } catch (error) {
        console.error('Error resolving free plan destination:', error);
        navigate('/dashboard');
      }
    } else {
      // No amount in the URL — the server resolves the price from planId.
      trackCheckoutStart({ source: 'pricing_page', planId: plan.id });
      navigate(`/checkout?planId=${plan.id}&planName=${encodeURIComponent(plan.name)}`);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden font-space">
      {/* Hero, compact on mobile */}
      <section className="relative pt-16 pb-8 sm:pt-32 sm:pb-24">
        <GeometricBackground />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <div className="inline-block px-4 py-1.5 sm:px-6 sm:py-2 bg-black text-white text-xs sm:text-sm font-bold tracking-widest uppercase rounded-full mb-4 sm:mb-6 animate-pulse">
            {t('pricing.hero.badge', { defaultValue: t('pricing.header.badge') })}
          </div>
          <h1 className="text-3xl sm:text-6xl font-black text-black tracking-tighter mb-3 sm:mb-6">
            {t('pricing.hero.title', { defaultValue: t('pricing.header.title') })}
          </h1>
          <p className="max-w-2xl mx-auto text-base sm:text-xl text-black/60 font-medium leading-relaxed">
            {t('pricing.hero.subtitle', { defaultValue: t('pricing.header.subtitle') })}
          </p>
        </div>
      </section>

      {/* ── MOBILE plan cards ── */}
      <section ref={mobileCardsRef} className={`lg:hidden relative z-10 pb-8 transition-all duration-700 ease-out ${mobileCardsAreVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="px-4 space-y-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border-2 overflow-hidden transition-all duration-300 active:scale-[0.98]
                ${plan.isPopular ? 'bg-black text-white border-black' : 'bg-white text-black border-black/20'}
              `}
            >
              {/* Suppressed on the popular card: it would sit on top of the
                  blue "Most Popular" banner bar. */}
              {plan.isPopular && (
                <div className="bg-[#3A86FF] text-white px-4 py-1.5 flex items-center gap-1.5 text-xs font-black tracking-widest uppercase">
                  <Zap className="w-3 h-3 fill-current" />
                  {t('pricing.plans.labels.popular')}
                </div>
              )}

              <div className="p-4">
                {/* Plan header row */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-lg font-black uppercase tracking-tight">{plan.name}</h3>
                    <p className={`text-[10px] mt-0.5 font-semibold uppercase tracking-wider ${plan.isPopular ? 'text-white/40' : 'text-black/40'}`}>{plan.description}</p>
                    {/* Inline, not in the corner: there it would cover the price. */}
                    {plan.badge && (
                      <span className="inline-block mt-1.5 bg-amber-400 text-black px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest uppercase">
                        {plan.badge}
                      </span>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-baseline gap-1 justify-end">
                      <span className="text-2xl font-black">{plan.price}</span>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest whitespace-nowrap ${plan.isPopular ? 'text-white/50' : 'text-black/40'}`}>/ {plan.cycle}</span>
                  </div>
                </div>

                {/* Outcome headline + benefits */}
                <p className={`text-sm font-black mb-2 leading-snug ${plan.isPopular ? 'text-white' : 'text-black'}`}>{plan.headline}</p>
                <ul className="space-y-1.5 mb-3">
                  {plan.benefits.map((benefit, idx) => (
                    <li key={idx} className={`text-xs font-medium flex items-start gap-1.5 ${plan.isPopular ? 'text-white/75' : 'text-black/65'}`}>
                      <span className={`flex-shrink-0 font-black mt-px ${plan.isPopular ? 'text-[#3A86FF]' : 'text-black'}`}>, </span>
                      {benefit}
                    </li>
                  ))}
                </ul>
                <p className={`text-[10px] font-semibold mb-3 ${plan.isPopular ? 'text-white/35' : 'text-black/35'}`}>{plan.trustLine || t('pricing.lineup.free.trustLine')}</p>

                {/* CTA button per card */}
                <button
                  onClick={() => handlePlanSelect(plan)}
                  className={`w-full py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all active:scale-95
                    ${plan.isPopular
                      ? 'bg-[#3A86FF] text-white'
                      : plan.id === 'free'
                        ? 'bg-white text-black border-2 border-black'
                        : 'bg-black text-white'
                    }`}
                >
                  {plan.id === 'free' ? t('pricing.lineup.free.name') : t('pricing.cta.button')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── DESKTOP plan cards ── */}
      <section ref={desktopCardsRef} className={`hidden lg:block relative z-10 py-12 transition-all duration-1000 ease-out ${desktopCardsAreVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className={`grid ${plans.length === 4 ? 'grid-cols-4' : 'grid-cols-3'} gap-6 items-stretch`}>
            {plans.map((plan) => {
              const isSelected = selectedPlan?.id === plan.id;
              return (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan)}
                  className={`group relative flex flex-col p-8 rounded-3xl cursor-pointer transition-all duration-500 border-[3px]
                    ${isSelected
                      ? 'border-[#3A86FF] -translate-y-6 shadow-[30px_30px_0px_0px_rgba(58,134,255,0.2)]'
                      : 'border-black hover:-translate-y-4 hover:shadow-[20px_20px_0px_0px_rgba(0,0,0,0.1)]'}
                    ${plan.isPopular ? 'bg-black text-white' : 'bg-white text-black'}
                  `}
                >
                  {/* Suppressed on the popular card: the centered "Most Popular"
                      badge occupies the same strip and the two collide. */}
                  {plan.badge && !plan.isPopular && (
                    <div className="absolute -top-4 right-4 bg-amber-400 text-black px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase border border-amber-500">
                      {plan.badge}
                    </div>
                  )}
                  {plan.isPopular && (
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#3A86FF] text-white px-6 py-2 rounded-full text-xs font-black tracking-widest uppercase flex items-center gap-2 border-2 border-black">
                      <Zap className="w-4 h-4 fill-current" />
                      {t('pricing.plans.labels.popular')}
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute top-4 right-4 bg-[#3A86FF] text-white p-1 rounded-full">
                      <Check className="w-5 h-5 stroke-[4px]" />
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="text-2xl font-black mb-1 tracking-tight uppercase">{plan.name}</h3>
                    <p className={`text-xs font-semibold uppercase tracking-widest ${plan.isPopular ? 'text-white/40' : 'text-black/40'}`}>{plan.description}</p>
                  </div>
                  <div className="mb-8 flex flex-col">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-black tracking-tighter">{plan.price}</span>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest opacity-60 mt-1">/ {plan.cycle}</span>
                  </div>
                  <div className="mt-4 flex-grow flex flex-col">
                    <p className={`text-base font-black mb-3 leading-snug ${plan.isPopular ? 'text-white' : 'text-black'}`}>{plan.headline}</p>
                    <ul className="space-y-3 flex-grow">
                      {plan.benefits.map((benefit, idx) => (
                        <li key={idx} className={`flex items-start gap-2 text-sm font-medium ${plan.isPopular ? 'text-white/75' : 'text-black/65'}`}>
                          <span className={`flex-shrink-0 font-black text-base leading-none mt-0.5 ${plan.isPopular ? 'text-[#3A86FF]' : 'text-black'}`}>, </span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    <p className={`mt-6 text-xs font-semibold ${plan.isPopular ? 'text-white/30' : 'text-black/30'}`}>{plan.trustLine || t('pricing.lineup.free.trustLine')}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`mt-20 flex flex-col items-center transition-all duration-700 ${selectedPlan ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
            <p className="mb-4 font-black text-black/40 uppercase tracking-widest text-sm flex items-center gap-2">
              {t('pricing.cta.ready')} <span className="text-black">{selectedPlan?.name}</span>?
            </p>
            <button
              onClick={() => selectedPlan && handlePlanSelect(selectedPlan)}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={(e) => handleMouseLeave(e, false)}
              style={{ position: 'relative', cursor: 'pointer', overflow: 'hidden' }}
              className="group inline-flex items-center gap-4 px-12 py-5 bg-black text-white border-[3px] border-black rounded-2xl font-black text-2xl transition-all duration-500 hover:scale-105 hover:shadow-[15px_15px_0px_0px_rgba(58,134,255,1)]"
            >
              <Rocket className="w-7 h-7 relative z-10" />
              <span className="relative z-10">{t('pricing.cta.button')}</span>
              <ChevronRight className="w-7 h-7 relative z-10 group-hover:translate-x-2 transition-transform" />
              <span className="shine-effect" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)', transform: 'translateX(-100%)', transition: 'transform 0.6s ease', pointerEvents: 'none' }} />
            </button>
          </div>
        </div>
      </section>

      <section ref={reviewsRef} className={`py-24 bg-gray-50/50 transition-all duration-1000 ${reviewsAreVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-4 mb-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-black text-black mb-4 tracking-tight">{t('home.testimonials.title')}</h2>
            <p className="text-lg text-black/60 max-w-2xl mx-auto font-medium">{t('home.testimonials.subtitle')}</p>
        </div>
        <FlowingReviewsTicker />
      </section>

      <section ref={faqRef} className={`py-20 transition-all duration-1000 ${faqsAreVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-black text-black text-center mb-12 tracking-tight">
            {t('pricing.faq.title')}
          </h2>
          <div className="space-y-4 mb-12">
            {pricingFaqs.map((faq, index) => (
              <details key={index} className="group bg-white border-[3px] border-black rounded-2xl overflow-hidden cursor-pointer transition-all hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                <summary className="flex items-center justify-between p-6 font-black text-lg text-black list-none">
                  {faq.q}
                  <div className="bg-black text-white rounded-lg p-1 group-open:rotate-180 transition-transform">
                    <ChevronDown className="w-5 h-5" />
                  </div>
                </summary>
                <div className="px-6 pb-6 text-black/70 leading-relaxed font-medium border-t border-black/5 pt-4">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/faq')}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={(e) => handleMouseLeave(e, false)}
              style={{ position: 'relative', cursor: 'pointer', overflow: 'hidden' }}
              className="group inline-flex items-center gap-3 px-10 py-4 bg-black text-white border-[3px] border-black rounded-xl font-space font-black text-lg transition-all duration-500 hover:scale-105 hover:shadow-2xl cursor-pointer"
            >
              <HelpCircle className="w-6 h-6 relative z-10" />
              <span className="relative z-10">{t('pricing.faq.all', { defaultValue: t('home.faq.viewAll') })}</span>
              <ChevronRight className="w-6 h-6 relative z-10 group-hover:translate-x-1 transition-transform" />
              <span className="shine-effect" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)', transform: 'translateX(-100%)', transition: 'transform 0.5s ease', pointerEvents: 'none' }} />
            </button>
          </div>
        </div>
      </section>

      <div className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 z-50">
        <button onClick={() => setShowReviewForm(true)} className="group relative flex items-center gap-3 bg-black text-white p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 sm:border-[3px] border-white shadow-2xl transition-all duration-500 hover:scale-110 active:scale-95 cursor-pointer">
          <Plus className="w-5 h-5 sm:w-6 sm:h-6 font-black" />
          <span className="hidden sm:inline max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-xs transition-all duration-500 font-bold uppercase tracking-tighter">{t('pricing.reviews.write')}</span>
        </button>
      </div>

      {showReviewForm && <ReviewForm onClose={() => setShowReviewForm(false)} onReviewSubmitted={() => window.location.reload()} />}
    </div>
  );
};

export default PricingPage;